import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  TextField,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useAddItemMutation, useUpdateItemMutation } from "../../../hooks/base-hooks";
import { fetchItem } from "../../../support/queries";
import { CacheKey } from "../../../types/cache-keys";
import { ProductFeature, ProductLicense, ProductVersion } from "../../../types/product";
import AdminConfirmDialog from "../Common/AdminConfirmDialog";
import ResourceRelationPanel from "../Panels/ResourceRelationPanel";
import {
  ProductFeatureVersionLink,
  useAddProductFeatureVersionMutation,
  useDeleteProductFeatureVersionMutation,
  useFetchLicenses,
  useFetchProductFeatures,
  useFetchProductVersionFeatures,
  useFetchSingleProduct,
} from "../../../hooks/product-hooks";
import { VersionScope } from "../../../types/snackableCourse";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  productId: number;
  versionId?: number;
  onClose: () => void;
  onSaved: (savedVersion?: ProductVersion) => void;
  readOnly?: boolean;
  versionScope?: VersionScope | null;
};

type ProductVersionForm = {
  version: string;
  released: string;
  last_update: string;
  end_of_life: string;
  release_notes_url: string;
  configuration_guide: string;
  api_documentation: string;
};

type FeatureVersionDraftRow = {
  rowId: string;
  joinId?: number | null;
  featureId: number | null;
  licenseId: number | null;
};

const emptyForm: ProductVersionForm = {
  version: "",
  released: "",
  last_update: "",
  end_of_life: "",
  release_notes_url: "",
  configuration_guide: "",
  api_documentation: "",
};

const makeRowId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `feature-version-row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const normalizeDate = (value?: string | Date | null) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }
  try {
    return value.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const toId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === "object" && value !== null && "id" in value) {
    return toId((value as { id?: unknown }).id);
  }

  return null;
};

const getJoinFeatureId = (join: ProductFeatureVersionLink): number | null => {
  return (
    toId((join as any)?.product_feature) ||
    toId((join as any)?.feature) ||
    null
  );
};

const getJoinLicenseId = (join: ProductFeatureVersionLink): number | null => {
  return (
    toId((join as any)?.license) ||
    toId((join as any)?.product_license) ||
    null
  );
};

const getProductScopedLicenses = ({
                                    product,
                                    allLicenses,
                                  }: {
  product: any;
  allLicenses: ProductLicense[];
}): ProductLicense[] => {
  const fromProduct = Array.isArray(product?.license)
    ? product.license
    : Array.isArray(product?.licenses)
      ? product.licenses
      : [];

  if (fromProduct.length) {
    return fromProduct.filter(Boolean);
  }

  const fromFeatures = Array.isArray(product?.feature)
    ? product.feature.flatMap((feature: any) => {
      if (Array.isArray(feature?.license)) return feature.license;
      if (Array.isArray(feature?.licenses)) return feature.licenses;
      return [];
    })
    : [];

  const uniqueById = new Map<number, ProductLicense>();

  fromFeatures.forEach((license: any) => {
    const id = toId(license);
    if (id == null) return;

    if (typeof license === "object" && license !== null) {
      uniqueById.set(id, license as ProductLicense);
      return;
    }

    const found = allLicenses.find((item) => Number(item.id) === Number(id));
    if (found) uniqueById.set(id, found);
  });

  if (uniqueById.size) {
    return Array.from(uniqueById.values());
  }

  return allLicenses ?? [];
};

const serializeRows = (rows: FeatureVersionDraftRow[]) => {
  return JSON.stringify(
    rows
      .filter((row) => row.featureId != null)
      .map((row) => ({
        featureId: row.featureId ?? null,
        licenseId: row.licenseId ?? null,
      }))
      .sort((a, b) => {
        const aKey = `${a.featureId ?? ""}-${a.licenseId ?? ""}`;
        const bKey = `${b.featureId ?? ""}-${b.licenseId ?? ""}`;
        return aKey.localeCompare(bKey);
      })
  );
};

export default function ProductVersionEditorDialog({
                                                     open,
                                                     mode,
                                                     productId,
                                                     versionId,
                                                     onClose,
                                                     onSaved,
                                                     readOnly = false,
                                                     versionScope = null,
                                                   }: Props) {
  const isEdit = mode === "edit" && !!versionId;

  const addVersion = useAddItemMutation("db/version", () => [
    CacheKey.ProductVersion,
    CacheKey.Product,
    productId,
  ]);

  const updateVersion = useUpdateItemMutation("db/version", () => [
    CacheKey.ProductVersion,
    CacheKey.Product,
    productId,
    String(versionId),
  ]);

  const { data: product } = useFetchSingleProduct(
    productId,
    {},
    { enabled: open && !!productId }
  );

  const {
    data: productFeatures = [],
    isLoading: productFeaturesLoading,
  } = useFetchProductFeatures(productId, {}, { enabled: open && !!productId });

  const {
    data: productLicenses = [],
    isLoading: productLicensesLoading,
  } = useFetchLicenses({}, { enabled: open });

  const scopedProductLicenses = useMemo(() => {
    return getProductScopedLicenses({
      product,
      allLicenses: productLicenses ?? [],
    });
  }, [product, productLicenses]);

  const {
    data: versionFeatureJoins = [],
    isLoading: versionFeatureJoinsLoading,
    refetch: refetchVersionFeatures,
  } = useFetchProductVersionFeatures(
    versionId,
    { detailed: true, size: 2000 },
    { enabled: open && isEdit && !!versionId }
  );

  const addFeatureVersion = useAddProductFeatureVersionMutation(productId, versionId);
  const deleteFeatureVersion = useDeleteProductFeatureVersionMutation(productId, versionId);

  const [form, setForm] = useState<ProductVersionForm>(emptyForm);
  const [initialForm, setInitialForm] = useState<ProductVersionForm>(emptyForm);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const [featureRows, setFeatureRows] = useState<FeatureVersionDraftRow[]>([]);
  const [initialFeatureRows, setInitialFeatureRows] = useState<FeatureVersionDraftRow[]>([]);

  const resolvedVersionScope: VersionScope = useMemo(() => {
    return {
      ...(versionScope || {}),
      product_id: productId,
      product_version_id: versionId ?? versionScope?.product_version_id ?? null,
      doc_version: form.version || versionScope?.doc_version || null,
      deployment_scope: versionScope?.deployment_scope || "appliance",
      generation_scope: "version",
      version_source: versionId ? "selected_product_version" : "new_product_version",
    };
  }, [versionScope, productId, versionId, form.version]);

  useEffect(() => {
    let active = true;

    const loadVersion = async () => {
      if (!open) return;

      if (!isEdit || !versionId) {
        setForm(emptyForm);
        setInitialForm(emptyForm);
        setFeatureRows([]);
        setInitialFeatureRows([]);
        return;
      }

      setLoadingVersion(true);
      try {
        const data = await fetchItem(`db/version/${versionId}`, { detailed: true });

        if (!active) return;

        const nextForm: ProductVersionForm = {
          version: data?.version ?? "",
          released: normalizeDate(data?.released),
          last_update: normalizeDate(data?.last_update),
          end_of_life: normalizeDate(data?.end_of_life),
          release_notes_url: data?.release_notes_url ?? "",
          configuration_guide: data?.configuration_guide ?? "",
          api_documentation: data?.api_documentation ?? "",
        };

        setForm(nextForm);
        setInitialForm(nextForm);
      } catch (error) {
        console.error("Failed to load product version:", error);
      } finally {
        if (active) setLoadingVersion(false);
      }
    };

    loadVersion();

    return () => {
      active = false;
    };
  }, [open, isEdit, versionId]);

  useEffect(() => {
    if (!open || !isEdit) return;

    const rows: FeatureVersionDraftRow[] = (versionFeatureJoins ?? [])
      .map((join) => {
        const featureId = getJoinFeatureId(join);
        if (featureId == null) return null;

        return {
          rowId: String((join as any).id ?? makeRowId()),
          joinId: toId((join as any).id),
          featureId,
          licenseId: getJoinLicenseId(join),
        };
      })
      .filter(Boolean) as FeatureVersionDraftRow[];

    setFeatureRows(rows);
    setInitialFeatureRows(rows);
  }, [open, isEdit, versionFeatureJoins]);

  const saving =
    addVersion.isPending ||
    updateVersion.isPending ||
    addFeatureVersion.isPending ||
    deleteFeatureVersion.isPending;

  const loadingRelations =
    productFeaturesLoading ||
    productLicensesLoading ||
    versionFeatureJoinsLoading;

  const disabledAll = saving || loadingVersion || readOnly;

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.version !== initialForm.version ||
      form.released !== initialForm.released ||
      form.last_update !== initialForm.last_update ||
      form.end_of_life !== initialForm.end_of_life ||
      form.release_notes_url !== initialForm.release_notes_url ||
      form.configuration_guide !== initialForm.configuration_guide ||
      form.api_documentation !== initialForm.api_documentation ||
      serializeRows(featureRows) !== serializeRows(initialFeatureRows)
    );
  }, [
    form,
    initialForm,
    featureRows,
    initialFeatureRows,
  ]);

  const selectedFeatureIds = useMemo(() => {
    return new Set(
      featureRows
        .map((row) => row.featureId)
        .filter((id): id is number => id != null)
    );
  }, [featureRows]);

  const getAvailableFeaturesForRow = (rowId: string) => {
    const currentRow = featureRows.find((row) => row.rowId === rowId);
    const currentFeatureId = currentRow?.featureId ?? null;

    return (productFeatures ?? []).filter((feature: any) => {
      const featureId = Number(feature.id);
      if (currentFeatureId != null && featureId === Number(currentFeatureId)) return true;
      return !selectedFeatureIds.has(featureId);
    });
  };

  const canAddFeatureRow = useMemo(() => {
    const totalFeatures = (productFeatures ?? []).length;
    const selectedCount = featureRows.filter((row) => row.featureId != null).length;
    return selectedCount < totalFeatures;
  }, [productFeatures, featureRows]);

  const handleReleasedChange = (released: string) => {
    setForm((prev) => ({
      ...prev,
      released,
      last_update: prev.last_update || released,
    }));
  };

  const addFeatureRow = () => {
    if (readOnly) return;

    setFeatureRows((prev) => [
      ...prev,
      {
        rowId: makeRowId(),
        joinId: null,
        featureId: null,
        licenseId: null,
      },
    ]);
  };

  const removeFeatureRow = (rowId: string) => {
    if (readOnly) return;
    setFeatureRows((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const updateFeatureRow = (rowId: string, patch: Partial<FeatureVersionDraftRow>) => {
    if (readOnly) return;

    setFeatureRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? {
            ...row,
            ...patch,
          }
          : row
      )
    );
  };

  const syncVersionFeatures = async (targetVersionId: number) => {
    const desiredRows = featureRows
      .filter((row) => row.featureId != null)
      .map((row) => ({
        featureId: Number(row.featureId),
        licenseId: row.licenseId ?? null,
      }));

    const currentJoins = isEdit && versionId === targetVersionId
      ? versionFeatureJoins ?? []
      : [];

    await Promise.all(
      currentJoins
        .filter((join: any) => !!join?.id)
        .map((join: any) => deleteFeatureVersion.mutateAsync({ id: join.id } as any))
    );

    await Promise.all(
      desiredRows.map(({ featureId, licenseId }) =>
        addFeatureVersion.mutateAsync({
          product_feature: featureId,
          feature: featureId,
          version: targetVersionId,
          license: licenseId,
          product_license: licenseId,
        } as any)
      )
    );

    if (isEdit) {
      await refetchVersionFeatures();
    }
  };

  const handleSave = async () => {
    if (readOnly) return;

    const payload = {
      product: productId,
      version: form.version || null,
      released: form.released || null,
      last_update: form.last_update || form.released || null,
      end_of_life: form.end_of_life || null,
      release_notes_url: form.release_notes_url || null,
      configuration_guide: form.configuration_guide || null,
      api_documentation: form.api_documentation || null,
    };

    try {
      if (!isEdit) {
        const created: any = await addVersion.mutateAsync(payload);
        const createdVersionId = created?.id;

        if (createdVersionId) {
          await syncVersionFeatures(createdVersionId);
        }

        onClose();
        setTimeout(() => onSaved(created), 0);
        return;
      }

      if (versionId) {
        const updated: any = await updateVersion.mutateAsync({
          ...payload,
          id: versionId,
        });

        await syncVersionFeatures(versionId);

        onClose();
        setTimeout(() => onSaved(updated), 0);
      }
    } catch (error) {
      console.error("Failed to save product version:", error);
    }
  };

  const resetAndClose = () => {
    setForm(emptyForm);
    setInitialForm(emptyForm);
    setFeatureRows([]);
    setInitialFeatureRows([]);
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleClose = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const title = readOnly
    ? !isEdit
      ? "View Product Version"
      : `View Version: ${form.version || `#${versionId}`}`
    : !isEdit
      ? "Create Product Version"
      : `Edit Version: ${form.version || `#${versionId}`}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent>
          {(loadingVersion || loadingRelations) && (
            <Box display="flex" alignItems="center" gap={1} py={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading...</Typography>
            </Box>
          )}

          <TextField
            fullWidth
            required
            margin="normal"
            label="Version"
            value={form.version}
            onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))}
            disabled={disabledAll}
          />

          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
            <TextField
              fullWidth
              required
              margin="normal"
              type="date"
              label="Released"
              InputLabelProps={{ shrink: true }}
              value={form.released}
              onChange={(e) => handleReleasedChange(e.target.value)}
              disabled={disabledAll}
            />
            <TextField
              fullWidth
              margin="normal"
              type="date"
              label="Last Update"
              InputLabelProps={{ shrink: true }}
              value={form.last_update}
              onChange={(e) => setForm((p) => ({ ...p, last_update: e.target.value }))}
              disabled={disabledAll}
            />
          </Box>

          {isEdit && (
            <TextField
              fullWidth
              margin="normal"
              type="date"
              label="End of Life"
              InputLabelProps={{ shrink: true }}
              value={form.end_of_life}
              onChange={(e) => setForm((p) => ({ ...p, end_of_life: e.target.value }))}
              disabled={disabledAll}
            />
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Release Notes URL"
            value={form.release_notes_url}
            onChange={(e) => setForm((p) => ({ ...p, release_notes_url: e.target.value }))}
            disabled={disabledAll}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Configuration Guide"
            value={form.configuration_guide}
            onChange={(e) => setForm((p) => ({ ...p, configuration_guide: e.target.value }))}
            disabled={disabledAll}
          />

          <TextField
            fullWidth
            margin="normal"
            label="API Documentation"
            value={form.api_documentation}
            onChange={(e) => setForm((p) => ({ ...p, api_documentation: e.target.value }))}
            disabled={disabledAll}
          />

          <Divider sx={{ my: 3 }} />

          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Features Available in this Version ({featureRows.filter((row) => row.featureId != null).length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add one row per feature available in this version. Optionally select the license that applies to the feature/version mapping.
                </Typography>
              </Box>

              {!readOnly && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={addFeatureRow}
                  disabled={disabledAll || productFeaturesLoading || !canAddFeatureRow}
                >
                  + Add Feature
                </Button>
              )}
            </Box>

            <Box display="grid" gap={2}>
              {featureRows.map((row, index) => {
                const availableFeatures = getAvailableFeaturesForRow(row.rowId);

                return (
                  <Box
                    key={row.rowId}
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr auto" }}
                    gap={2}
                    alignItems="start"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label={`Feature ${index + 1}`}
                      value={row.featureId ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateFeatureRow(row.rowId, {
                          featureId: value === "" ? null : Number(value),
                          licenseId: null,
                        });
                      }}
                      disabled={disabledAll || productFeaturesLoading}
                    >
                      <MenuItem value="">
                        <em>Select feature</em>
                      </MenuItem>

                      {availableFeatures.map((feature: ProductFeature) => (
                        <MenuItem key={feature.id} value={feature.id}>
                          {feature.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      size="small"
                      label="License"
                      value={row.licenseId ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateFeatureRow(row.rowId, {
                          licenseId: value === "" ? null : Number(value),
                        });
                      }}
                      disabled={disabledAll || productLicensesLoading || row.featureId == null}
                      helperText="Optional product license"
                    >
                      <MenuItem value="">
                        <em>No license</em>
                      </MenuItem>

                      {(scopedProductLicenses ?? []).map((license: ProductLicense) => (
                        <MenuItem key={license.id} value={license.id}>
                          {license.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {!readOnly && (
                      <Tooltip title="Remove">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => removeFeatureRow(row.rowId)}
                            disabled={disabledAll}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}

              {!featureRows.length && (
                <Alert severity="info">
                  No features are currently mapped to this version. Click “+ Add Feature” to add one.
                </Alert>
              )}
            </Box>

            {!productFeaturesLoading && productFeatures.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No product features exist yet. Create product features before assigning them to versions.
              </Typography>
            )}
          </Box>

          {isEdit && versionId && (
            <>
              <Divider sx={{ my: 3 }} />

              <ResourceRelationPanel
                joinEndpoint="db/product-to-resource"
                joinCacheKey={CacheKey.ProductToResource}
                parentField="product"
                parentId={productId}
                title="Product Resources for this Version"
                showNameColumn={true}
                maxHeight={300}
                invalidateKeys={[CacheKey.Product, CacheKey.ProductVersion]}
                readOnly={readOnly}
                versionScope={resolvedVersionScope}
                showVersionScopeColumn={true}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !form.version || !form.released}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message="You have unsaved changes in this product version editor. If you continue, your changes will be lost."
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={resetAndClose}
        onCancel={() => setConfirmCloseOpen(false)}
      />
    </>
  );
}